import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MatchTable } from '@/components/shared/MatchTable';
import { ManualConsumptionDialog } from './ManualConsumptionDialog';
import type {
  FipeVehicle,
  ManualConsumptionRecord,
  MatchFilters,
  VehicleMatch,
} from '@/types';
import { formatManualConsumptionSummary } from '@/utils/helpers';
import type { ManualConsumptionTarget } from './ManualConsumptionDialog';

interface MatchesViewProps {
  matches: VehicleMatch[];
  allMatches: VehicleMatch[];
  unmatchedVehicles: FipeVehicle[];
  filters: MatchFilters;
  onFiltersChange: (filters: Partial<MatchFilters>) => void;
  onVerify: (matchId: number, isVerified: boolean) => void;
  onDelete: (matchId: number) => void;
  onManualConsumptionSaved: (record: ManualConsumptionRecord) => Promise<void> | void;
}

export function MatchesView({
  matches,
  allMatches,
  unmatchedVehicles,
  filters,
  onFiltersChange,
  onVerify,
  onDelete,
  onManualConsumptionSaved,
}: MatchesViewProps) {
  const [selectedTarget, setSelectedTarget] = useState<ManualConsumptionTarget | null>(null);

  const matchedModelIds = new Set(allMatches.map((match) => match.model_id));
  const unmatchedWithoutAnyMatch = unmatchedVehicles.filter(
    (vehicle) => !matchedModelIds.has(vehicle.model_id)
  );
  const nonVerifiedCount = allMatches.filter((match) => !match.is_verified).length;

  const openManualForMatch = (match: VehicleMatch) => {
    setSelectedTarget({
      kind: 'match',
      model_id: match.model_id,
      match_id: match.id,
      pbev_id: match.pbev_id,
      fipe_id: match.fipe_id,
      fipe_vehicle: match.fipe_vehicle,
      pbev_vehicle: match.pbev_vehicle,
      confidence_score: match.confidence_score,
      ano: match.ano_referencia,
      manual_consumption: match.manual_consumption,
    });
  };

  const openManualForUnmatched = (vehicle: FipeVehicle) => {
    setSelectedTarget({
      kind: 'unmatched',
      model_id: vehicle.model_id,
      fipe_id: vehicle.fipe_id,
      fipe_vehicle: vehicle.vehicle,
      ano: vehicle.ano,
      manual_consumption: vehicle.manual_consumption,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <CardTitle>All Matches</CardTitle>
              <div className="mt-2 text-sm text-muted-foreground">
                {matches.length} visible matches • {nonVerifiedCount} non-verified total • {unmatchedWithoutAnyMatch.length} FIPE vehicles without any match row
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Search
                </div>
                <Input
                  value={filters.search}
                  onChange={(event) => onFiltersChange({ search: event.target.value })}
                  placeholder="Search FIPE or PBEV labels"
                />
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Verification state
                </div>
                <Select
                  value={filters.verifiedState}
                  onValueChange={(value: MatchFilters['verifiedState']) =>
                    onFiltersChange({ verifiedState: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All matches</SelectItem>
                    <SelectItem value="verified">Verified only</SelectItem>
                    <SelectItem value="unverified">Non-verified only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Min confidence
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={filters.minConfidence}
                    onChange={(event) =>
                      onFiltersChange({ minConfidence: Number(event.target.value) })
                    }
                    className="w-32"
                  />
                  <span className="text-sm font-medium">
                    {(filters.minConfidence * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/20 px-4 py-3">
                <Checkbox
                  checked={filters.hasManualConsumption}
                  onCheckedChange={(checked) =>
                    onFiltersChange({ hasManualConsumption: Boolean(checked) })
                  }
                />
                <div>
                  <div className="text-sm font-medium">Only rows with manual consumption</div>
                  <div className="text-xs text-muted-foreground">
                    Useful for reviewing approved overrides.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <MatchTable
            matches={matches}
            onVerify={onVerify}
            onDelete={onDelete}
            onEditManualConsumption={openManualForMatch}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Direct Manual Entry for Unmatched FIPE Vehicles</CardTitle>
              <div className="mt-2 text-sm text-muted-foreground">
                Use this section when there is no match row yet and you still need a reviewed consumption override.
              </div>
            </div>
            <Badge variant="outline">{unmatchedWithoutAnyMatch.length} vehicles</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {unmatchedWithoutAnyMatch.length === 0 ? (
            <div className="rounded-lg border border-border/70 bg-muted/20 p-6 text-sm text-muted-foreground">
              Every currently visible unresolved FIPE vehicle already has at least one match row.
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {unmatchedWithoutAnyMatch.map((vehicle) => (
                <div
                  key={vehicle.model_id}
                  className="rounded-lg border border-border/70 bg-muted/10 p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="font-medium">{vehicle.vehicle}</div>
                      <div className="text-xs text-muted-foreground">
                        Model ID: {vehicle.model_id}
                        {vehicle.fipe_id ? ` • FIPE ID: ${vehicle.fipe_id}` : ''}
                        {vehicle.ano ? ` • Year: ${vehicle.ano}` : ''}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatManualConsumptionSummary(vehicle.manual_consumption)}
                      </div>
                    </div>

                    <Button size="sm" onClick={() => openManualForUnmatched(vehicle)}>
                      Edit manual data
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ManualConsumptionDialog
        open={Boolean(selectedTarget)}
        target={selectedTarget}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTarget(null);
          }
        }}
        onSaved={async (record) => {
          await onManualConsumptionSaved(record);
          setSelectedTarget(null);
        }}
      />
    </div>
  );
}
